/* GASSISA
=========================================================================================
* Descripción: Modal reporte de Entradas/Salidas por periodo para Administración de Cortes.
*              Fuente única: VistaMovimientosAlmacenesExhibidores.
*              - TipoMovimiento: 1 = entradas, 0 = salidas.
*              - ConceptoMovimiento por LIKE: "Entrada desde PC", "Salida desde PC",
*                "Entrada por Etiquetado". Cualquier otro concepto queda excluido
*                (traslados, ventas, etc.).
*              - Rango por FechaRegistro, agrupado sumando CantidadEntrada/CantidadSalida.
*              - Descripción y Almacén/Exhibidor se envían con TRIM + UPPER para que el
*                backend compare con UPPER(LTRIM(RTRIM(columna))) LIKE.
* Contrato backend (POST TenantGeneradorpdf/reporteentradassalidasperiodo):
*   Filtra MovimientosAlmacenesExhibidores por FechaRegistro, TipoMovimiento y
*   ConceptoMovimiento LIKE 'DESDE PC' (excluye traslados, ventas, apartados,
*   devoluciones, prestamos, entregas e ingresos rapidos). Filtros opcionales
*   descripcion/clave, almacen y concepto con TRIM + UPPER. Detalle agrupado
*   por almacen, concepto y folio con costos compra/venta.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 18/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 18/09/2026 | Lic. José Luis Aragón Cervantes  | Filtro por concepto con LIKE
*      3       | 18/09/2026 | Lic. José Luis Aragón Cervantes  | Modal permanece abierto tras generar
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import React, { Fragment, useState, useEffect } from "react";
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { apiReporteEntradasSalidasPeriodo } from "../../settings/ApiConfig";

const modalStyle = {
    position: 'fixed',
    top: '5%',
    left: '25%',
    width: '50vw',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

const TIPOS_MOVIMIENTO = ["ENTRADAS", "SALIDAS", "AMBOS"];

const TIPO_A_TIPOMOVIMIENTO = {
    ENTRADAS: 1,
    SALIDAS: 0,
    AMBOS: null,
};

export default function ModalReporteEntradasSalidasPeriodo(props) {
    const { openModal = false, onClose } = props;

    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { PostFileRequest } = useRequest();
    const { listaAlmacenesExhibidoresGeneral, handleObtenerAlmacenesExhibidores } = useInventarios();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [tipoMovimiento, setTipoMovimiento] = useState("AMBOS");
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [almacen, setAlmacen] = useState("TODOS");
    const [descripcionFiltro, setDescripcionFiltro] = useState("");
    const [conceptoFiltro, setConceptoFiltro] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            const hoy = new Date();
            setFechaFin(hoy);
            if (!listaAlmacenesExhibidoresGeneral || listaAlmacenesExhibidoresGeneral.length === 0) {
                handleObtenerAlmacenesExhibidores("GENERAL");
            }
            setTimeout(() => setModalVisible(true), 50);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    const getFechaLocal = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleGenerarReporte = async () => {
        if (fechaFin < fechaInicio) {
            handleOpenAlert("La fecha final no puede ser anterior a la fecha inicial", "warning");
            return;
        }
        if (!TIPOS_MOVIMIENTO.includes(tipoMovimiento)) {
            handleOpenAlert("Seleccione el tipo de movimiento: ENTRADAS, SALIDAS o AMBOS", "warning");
            return;
        }

        setLoading(true);
        try {
            // TRIM + UPPER: el backend debe comparar con
            // UPPER(LTRIM(RTRIM(Descripcion))) y UPPER(LTRIM(RTRIM(AlmacenExhibidor/Nombre))) por LIKE.
            const data = {
                tipo: tipoMovimiento,
                tipomovimiento: TIPO_A_TIPOMOVIMIENTO[tipoMovimiento],
                fechainicio: getFechaLocal(fechaInicio),
                fechafin: getFechaLocal(fechaFin),
                almacen: (almacen || "TODOS").trim().toUpperCase() || "TODOS",
                filtrodescripcion: (descripcionFiltro || "").trim().toUpperCase(),
                filtroconcepto: (conceptoFiltro || "").trim().toUpperCase(),
                sucursal: parametrosCajaForm?.sucursal ?? '',
                usuario: usuarioData?.Usuario ?? '',
                tipousuario: "ADMINISTRADOR",
            };

            const result = await PostFileRequest({
                url: apiReporteEntradasSalidasPeriodo,
                body: data,
                filename: `ReporteEntradasSalidas-${getFechaLocal(fechaInicio)}-${getFechaLocal(fechaFin)}.pdf`,
                openInNewTab: true,
                loader: "Cargando reporte...",
            });

            if (!result) throw new Error("No se pudo generar el reporte");

            handleOpenAlert("Reporte de entradas/salidas generado exitosamente", "success");
            // El modal permanece abierto para generar de nuevo; se cierra con
            // Cancelar, Escape o click fuera.
        } catch (error) {
            handleOpenAlert("Error al generar reporte: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Entradas / Salidas por periodo
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Fuente única: <strong>VistaMovimientosAlmacenesExhibidores</strong>. Entradas = TipoMovimiento 1
                            (Entrada desde PC + Entrada por Etiquetado), Salidas = TipoMovimiento 0 (Salida desde PC).
                            Traslados y demés movimientos quedan excluidos. Descripción y almacén se comparan con TRIM + UPPER por LIKE.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Autocomplete
                            options={TIPOS_MOVIMIENTO}
                            value={tipoMovimiento}
                            onChange={(_, valor) => setTipoMovimiento(valor || "AMBOS")}
                            sx={{ flex: '1 1 200px' }}
                            renderInput={(params) => (
                                <TextField autoComplete="off" {...params} label="Tipo de movimiento" size="small" />
                            )}
                        />
                        <Autocomplete
                            freeSolo
                            options={["TODOS", ...(listaAlmacenesExhibidoresGeneral || []).map((item) => item.nombre)]}
                            value={almacen}
                            onChange={(_, valor) => setAlmacen((valor || "TODOS").trim().toUpperCase() || "TODOS")}
                            onInputChange={(_, valor) => setAlmacen((valor || "TODOS").trim().toUpperCase() || "TODOS")}
                            sx={{ flex: '1 1 200px' }}
                            renderInput={(params) => (
                                <TextField autoComplete="off" {...params} label="Almacén o Exhibidor" size="small" />
                            )}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filtrar por descripción o clave <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(opcional, usa LIKE con TRIM + UPPER)</Typography>
                        </Typography>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            placeholder="Ej: Camisa, Pantalón, clave, etc."
                            value={descripcionFiltro}
                            onChange={(e) => setDescripcionFiltro(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filtrar por concepto de movimiento <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(opcional, usa LIKE con TRIM + UPPER)</Typography>
                        </Typography>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            placeholder="Ej: COMPRA, ETIQUETADO, etc."
                            value={conceptoFiltro}
                            onChange={(e) => setConceptoFiltro(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Rango de fechas (FechaRegistro):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Inicio</Typography>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Fin</Typography>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                    minDate={fechaInicio}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerarReporte}
                            disabled={loading}
                        >
                            {loading ? "Generando..." : "Generar Reporte PDF"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
    );
}
